import { QueryClient } from '@tanstack/react-query';

import { getErrorStatus } from '../utils/errorStatus';

const MAX_RETRIES = 3;

const HTTP_SERVICE_UNAVAILABLE = 503;

/**
 * Retry policy for queries (GET).
 *
 * GETs are safe to repeat, so a transient 503 or a network error (no HTTP
 * status: timeout, CORS, connection drop) is worth retrying. Every other
 * status, including the rest of the 5xx range, fails immediately: a 400 or
 * 409 won't succeed on a retry, and we don't want to mask a real failure.
 */
export const shouldRetryQuery = (failureCount: number, error: unknown): boolean => {
  if (failureCount >= MAX_RETRIES) return false;
  const status = getErrorStatus(error);
  return status === undefined || status === HTTP_SERVICE_UNAVAILABLE;
};

/**
 * Retry policy for mutations (POST/PUT/PATCH/DELETE).
 *
 * Retrying a write after a 502 or 504 can create a duplicate, so only a 503
 * is retried; a network error (no status) is also not retried, since we
 * cannot tell whether the write already landed. Every other status fails
 * immediately.
 */
export const shouldRetryMutation = (failureCount: number, error: unknown): boolean => {
  if (failureCount >= MAX_RETRIES) return false;
  return getErrorStatus(error) === HTTP_SERVICE_UNAVAILABLE;
};

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: shouldRetryQuery,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
    },
    mutations: {
      retry: shouldRetryMutation,
    },
  },
});
